import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";

import Container from "components/__reusable/Container";
import PictureThumbnail from "components/__reusable/PictureThumbnail";
import Pagination from "../__reusable/Pagination";
import LoadingDialog from "components/__reusable/LoadingDialog";
import PictureDialog from "./PictureDialog";
import { getPublishedPictures } from "services/pictureServices";
import type { Picture } from "types/Pictures";
import { PICTURE_PAGE_SIZE, PictureStudentOption } from "config/constants";
import PictureSearchInput from "components/__reusable/PictureSearchInput";
import { isValidDateString } from "utilities/DateFormatter/validate";
import { FaDownload, FaBookmark } from "react-icons/fa";
import { setHashtag } from "features/pictureSearch/pictureSearchSlice";

import { DownloadTarget, prepareZip, downloadBlob } from "utils/zip";
import useAppDispatch from "hooks/useAppDispatch";

// CSS styles for full-screen overlay effect
const overlayStyles = `
  .fullscreen-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 15;
    pointer-events: none;
  }
  
  #selection-area {
    position: relative;
    z-index: 20;
    background: white;
    padding: 1rem;
  }
  
  .thumbnail-area {
    position: relative;
    z-index: 10;
    background: white;
  }
  
  /* Responsive improvements for thumbnail area */
  @media (max-width: 640px) {
    .thumbnail-area {
      padding: 0.25rem;
    }
    
    .thumbnail-area .flex.flex-wrap {
      gap: 1px !important;
    }
  }
  
  @media (max-width: 375px) {
    .thumbnail-area {
      padding: 0.125rem;
    }
    
    .thumbnail-area .flex.flex-wrap {
      gap: 1px !important;
      justify-content: flex-start;
    }
  }
  
  /* Ensure dropdowns appear above all other content */
  .dropdown-container,
  [data-dropdown],
  .react-select__menu,
  .react-select__menu-list,
  .select-dropdown,
  .dropdown-menu {
    z-index: 50 !important;
  }
  
  /* Specifically target common dropdown patterns */
  div[role="listbox"],
  div[role="menu"],
  .dropdown-options,
  .select-options {
    z-index: 50 !important;
  }
`;

/* eslint-disable no-alert */

// Japanese UI labels
const LABELS = {
  SELECT: "選択",
  CANCEL: "キャンセル",
  BULK_DOWNLOAD: "一括ダウンロード",
  NO_PHOTOS_SELECTED: "選択された写真がありません",
  DOWNLOAD_COMPLETE: "ダウンロードが完了しました。",
  FILES_DOWNLOADED: "ファイルをダウンロードしました。",
  FILES_FAILED: "ファイルの取得に失敗しました。",
  DOWNLOAD_ERROR: "ダウンロードの準備中にエラーが発生しました",
};

/**
 * Pictures component: Displays and manages picture thumbnails with selection capabilities.
 * Features:
 * - Selection mode: Toggles between viewing (opens dialog) and multi-select mode
 * - Download: Uses original_picture URLs for high-quality downloads
 * - Zipping: Packages selected images (or all if none selected) into a downloadable zip
 */

/**
 * Get download targets based on selection state and currently visible pictures
 * @param pictures - Array of Picture objects
 * @param selectedIds - Set of selected picture IDs
 * @param isSelecting - Whether selection mode is active
 * @returns Array of download targets with original picture URLs
 */
export const getDownloadTargets = (
  pictures: Picture[],
  selectedIds: Set<string>,
  isSelecting: boolean,
): DownloadTarget[] => {
  if (!pictures || pictures.length === 0) {
    return [];
  }

  // If in selection mode with selected IDs, return only those pictures
  if (isSelecting && selectedIds.size > 0) {
    return pictures
      .filter((picture) => selectedIds.has(String(picture.id)))
      .map((picture) => ({
        id: String(picture.id),
        url: picture.original_picture,
        filename: `picture_${picture.id}.jpg`, // Create stable filename
      }));
  }

  // Otherwise return all visible pictures
  return pictures.map((picture) => ({
    id: String(picture.id),
    url: picture.original_picture,
    filename: `picture_${picture.id}.jpg`,
  }));
};

// Zip functionality moved to utils/zip.ts

type PhotoSize = "small" | "medium" | "large";

type Props = {
  startDate?: string;
  endDate?: string;
  classId?: number;
  studentId?: number | PictureStudentOption | null;
  hashtagId?: number;
  isFavorite?: boolean;
};

// Memoized and stable PhotoSizeSelector to prevent rc-slider errors
const MemoizedPhotoSizeSelector = React.memo(
  ({
    photoSize,
    onSizeChange,
    loading,
    isDownloading,
  }: {
    photoSize: PhotoSize;
    onSizeChange: (size: PhotoSize) => void;
    loading: boolean;
    isDownloading: boolean;
  }) => {
    const sizeValues = React.useMemo(
      () => ({ small: 0, medium: 1, large: 2 }),
      [],
    );
    const currentValue = sizeValues[photoSize];
    const canInteract = !loading && !isDownloading;

    const handleSliderChange = useCallback(
      (value: number | number[]) => {
        const numValue = Array.isArray(value) ? value[0] : value;
        const sizeKey = Object.keys(sizeValues).find(
          (key) => sizeValues[key as keyof typeof sizeValues] === numValue,
        ) as PhotoSize;
        if (sizeKey) {
          onSizeChange(sizeKey);
        }
      },
      [onSizeChange, sizeValues],
    );

    const decreaseSize = useCallback(() => {
      if (photoSize === "large") onSizeChange("medium");
      else if (photoSize === "medium") onSizeChange("small");
    }, [photoSize, onSizeChange]);

    const increaseSize = useCallback(() => {
      if (photoSize === "small") onSizeChange("medium");
      else if (photoSize === "medium") onSizeChange("large");
    }, [photoSize, onSizeChange]);

    return (
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={decreaseSize}
            disabled={photoSize === "small" || !canInteract}
            className="w-11 h-11 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg transition-colors duration-200 ease-out enabled:hover:bg-gray-100 disabled:opacity-40"
            aria-label="Decrease photo size"
            type="button"
          >
            <svg
              className="w-4 h-4 sm:w-3 sm:h-3 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 12H4"
              />
            </svg>
          </button>

          <div
            className="w-24 sm:w-32 py-2 relative flex items-center"
            style={{ minHeight: "24px" }}
          >
            <Slider
              min={0}
              max={2}
              step={1}
              value={currentValue}
              onChange={handleSliderChange}
              disabled={!canInteract}
              styles={{
                rail: {
                  backgroundColor: "#e5e7eb",
                  height: 6,
                  borderRadius: 3,
                },
                track: {
                  backgroundColor: canInteract ? "#3b82f6" : "#9ca3af",
                  height: 6,
                  borderRadius: 3,
                },
                handle: {
                  backgroundColor: "#ffffff",
                  border: `2px solid ${canInteract ? "#3b82f6" : "#9ca3af"}`,
                  width: 18,
                  height: 18,
                  marginTop: -6,
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  opacity: canInteract ? 1 : 0.5,
                },
              }}
            />
          </div>

          <button
            onClick={increaseSize}
            disabled={photoSize === "large" || !canInteract}
            className="w-11 h-11 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg transition-colors duration-200 ease-out enabled:hover:bg-gray-100"
            aria-label="Increase photo size"
            type="button"
          >
            <svg
              className="w-4 h-4 sm:w-3 sm:h-3 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  },
);
MemoizedPhotoSizeSelector.displayName = "MemoizedPhotoSizeSelector";

const Pictures: React.FC<Props> = (props: Props) => {
  const {
    startDate: propStartDate,
    endDate: propEndDate,
    classId: propClassId,
    studentId: propStudentId,
    hashtagId: propHashtagId,
    isFavorite: propIsFavorite,
  } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Local state management
  const [pictures, setPictures] = useState<Picture[]>([]);
  const [searchStartDate, setSearchStartDate] = useState<string>(
    propStartDate || "",
  );
  const [searchEndDate, setSearchEndDate] = useState<string>(propEndDate || "");
  const [searchClassId, setSearchClassId] = useState<number | undefined>(
    propClassId,
  );
  const [studentId, setStudentId] = useState<
    number | PictureStudentOption | null | undefined
  >(propStudentId);
  const [hashtagId, setHashtagId] = useState<number | undefined>(propHashtagId);
  const [isFavorite, setIsFavorite] = useState<boolean>(
    propIsFavorite || false,
  );
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewIndex, setViewIndex] = useState(-1);
  const [photoSize, setPhotoSize] = useState<PhotoSize>("medium");
  const [isSelecting, setIsSelecting] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  // Track if component is mounted to prevent initial duplicate calls
  const isMounted = useRef(false);

  // Current search values should reflect the actual component state, not just initial props
  const currentSearchValues = {
    startDate: searchStartDate,
    endDate: searchEndDate,
    classId: searchClassId,
    studentId: studentId,
    hashtagId: hashtagId,
    isFavorite: isFavorite,
  };

  // Fetch pictures function - wrapped in useCallback to avoid re-creation on each render
  const fetchPictures = useCallback(
    async (pageNum: number) => {
      setLoading(true);
      try {
        if (
          !searchStartDate ||
          !searchEndDate ||
          !isValidDateString(searchStartDate) ||
          !isValidDateString(searchEndDate)
        ) {
          setPictures([]);
          setTotal(0);
          return;
        }

        const [startYear, startMonth, startDay] = searchStartDate
          .split("-")
          .map(Number);

        const [endYear, endMonth, endDay] = searchEndDate
          .split("-")
          .map(Number);

        const startDate = new Date(
          startYear,
          startMonth - 1,
          startDay,
          0,
          0,
          0,
          0,
        );

        const endDate = new Date(
          endYear,
          endMonth - 1,
          endDay,
          23,
          59,
          59,
          999,
        );

        const result = await getPublishedPictures(
          pageNum + 1,
          startDate.toISOString(),
          endDate.toISOString(),
          searchClassId,
          studentId,
          hashtagId,
          isFavorite,
          true, // withPagination
        );

        setPictures(result.results);
        setTotal(result.count);
      } catch (e) {
        console.error(e);
        setPictures([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [
      searchStartDate,
      searchEndDate,
      searchClassId,
      studentId,
      hashtagId,
      isFavorite,
    ],
  );

  // Calculate pagination values
  const totalPages = Math.ceil(total / PICTURE_PAGE_SIZE);
  const currentPage = page + 1; // Convert from 0-based to 1-based
  const hasMoreContent = totalPages > 1 && currentPage < totalPages;

  // Main search effect - triggers when search parameters change
  useEffect(() => {
    if (searchStartDate && searchEndDate) {
      setPage(0); // Reset to first page when search parameters change
      fetchPictures(0);
    }
  }, [
    searchStartDate,
    searchEndDate,
    searchClassId,
    studentId,
    hashtagId,
    isFavorite,
    fetchPictures,
  ]);

  // Initialize component with props
  useEffect(() => {
    if (propStartDate && propEndDate) {
      setSearchStartDate(propStartDate);
      setSearchEndDate(propEndDate);
    }
    if (typeof propClassId !== "undefined") {
      setSearchClassId(propClassId);
    }
    if (typeof propStudentId !== "undefined") {
      setStudentId(propStudentId);
    }
    if (typeof propHashtagId !== "undefined") {
      setHashtagId(propHashtagId);
    }
    if (typeof propIsFavorite !== "undefined") {
      setIsFavorite(propIsFavorite);
    }
    // Mark component as mounted after initial setup
    isMounted.current = true;
  }, [
    propStartDate,
    propEndDate,
    propClassId,
    propStudentId,
    propHashtagId,
    propIsFavorite,
  ]);

  // Cleanup effect for slider
  useEffect(() => {
    return () => {
      // Clean up any lingering mouse events when component unmounts
      const cleanup = () => {
        document.removeEventListener("mousemove", () => {});
        document.removeEventListener("mouseup", () => {});
        document.removeEventListener("touchmove", () => {});
        document.removeEventListener("touchend", () => {});
      };
      cleanup();
    };
  }, []);

  // Helper function to get size classes based on photo size
  const getSizeClasses = (size: PhotoSize) => {
    switch (size) {
      case "small":
        return "w-14 h-14 sm:w-16 sm:h-16";
      case "medium":
        return "w-20 h-20 sm:w-24 sm:h-24";
      case "large":
        return "w-28 h-28 sm:w-32 sm:h-32";
      default:
        return "w-28 h-28 sm:w-32 sm:h-32";
    }
  };

  const handlePhotoSizeChange = useCallback((size: PhotoSize) => {
    setPhotoSize(size);
  }, []);

  // Helper functions for selection
  const isSelected = useCallback(
    (id: string): boolean => {
      return selectedIds.has(id);
    },
    [selectedIds],
  );

  const toggleSelected = useCallback((id: string): void => {
    setSelectedIds((prevIds) => {
      const newIds = new Set(prevIds);
      if (newIds.has(id)) {
        newIds.delete(id);
      } else {
        newIds.add(id);
      }
      return newIds;
    });
  }, []);

  const renderThumbnail = (picture: Picture) => {
    const sizeClasses = getSizeClasses(photoSize);
    const pictureId = String(picture.id);
    const isItemSelected = isSelected(pictureId);

    return (
      <div
        key={pictureId}
        className={`${sizeClasses} relative flex-shrink-0`}
        data-testid={`thumb-${pictureId}`}
        data-selected={isItemSelected ? "true" : "false"}
      >
        <button
          className="w-full h-full cursor-pointer rounded-none overflow-hidden relative"
          onClick={() => {
            if (isSelecting) {
              toggleSelected(pictureId);
            } else {
              viewPicture(picture.id);
            }
          }}
        >
          <PictureThumbnail
            imageSrc={picture.thumbnail_picture}
            name="picture"
            rounded={false}
          />
          {isSelecting && isItemSelected && (
            <span className="absolute inset-0 z-10 box-border border-2 border-red-600 pointer-events-none" />
          )}
          {picture.is_favorite && (
            <span
              className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 text-yellow-400"
              title="Favorite"
            >
              <FaBookmark className="text-[#EA6E7D]" size={14} />
            </span>
          )}
        </button>
      </div>
    );
  };

  const renderMoreContentIndicator = () => {
    if (!hasMoreContent) return null;

    const sizeClasses = getSizeClasses(photoSize);
    return (
      <div
        className={`${sizeClasses} flex items-center justify-center flex-shrink-0`}
      >
        <div
          className="flex flex-col items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300 rounded-md w-full h-full pointer-events-none"
          style={{ height: "100%" }}
        >
          <div className="text-gray-500 text-base sm:text-lg font-semibold">
            ・・・
          </div>
        </div>
      </div>
    );
  };

  const viewPicture = (pictureId: number) => {
    if (!pictures) return;
    const index = pictures.findIndex((picture) => picture.id === pictureId);
    setViewIndex(index);
  };

  const viewPreviousPicture = useCallback(() => {
    if (!pictures) return;
    let index = viewIndex;
    if (index === 0) return;
    index--;
    setViewIndex(index);
  }, [pictures, viewIndex]);

  const viewNextPicture = useCallback(() => {
    if (!pictures) return;
    let index = viewIndex;
    if (index === pictures.length - 1) return;
    index++;
    setViewIndex(index);
  }, [pictures, viewIndex]);

  const handleKeyboardEvent = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        viewNextPicture();
        return;
      }
      if (e.key === "ArrowLeft") {
        viewPreviousPicture();
        return;
      }
    },
    [viewNextPicture, viewPreviousPicture],
  );

  useEffect(() => {
    window.removeEventListener("keyup", handleKeyboardEvent);
    window.addEventListener("keyup", handleKeyboardEvent);
    return () => {
      window.removeEventListener("keyup", handleKeyboardEvent);
    };
  }, [handleKeyboardEvent]);

  const handleSearchChange = useCallback(
    (searchParams: {
      startDate?: string;
      endDate?: string;
      classId?: number;
      studentId?: number | PictureStudentOption | null;
      hashtagId?: number;
      isFavorite?: boolean;
    }) => {
      if (!isMounted.current) {
        return;
      }
      dispatch(setHashtag({ hashtagId: Number(searchParams.hashtagId) }));
      setSearchStartDate(searchParams.startDate || "");
      setSearchEndDate(searchParams.endDate || "");
      setSearchClassId(searchParams.classId);
      setStudentId(searchParams.studentId);
      setHashtagId(searchParams.hashtagId);
      setIsFavorite(searchParams.isFavorite || false);
    },
    [dispatch],
  );

  return (
    <div className="h-full w-full flex flex-col gap-2 sm:gap-3 lg:gap-4 relative">
      <style>{overlayStyles}</style>
      {isSelecting && <div className="fullscreen-overlay !m-0" />}
      <div className="px-1 sm:px-0">
        <PictureSearchInput
          onSearchChange={handleSearchChange}
          initialValues={currentSearchValues}
          isLoading={loading}
        />
      </div>
      <div id="selection-area" className="flex-1 flex flex-col px-1 sm:px-0">
        {pictures && (
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 mb-2">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 lg:gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                {total >= 1000 && (
                  <p className="text-red-600 text-sm font-medium">
                    {t("_published_picture._large_data_warning")}
                  </p>
                )}
                <p className="text-gray-700 font-medium text-sm sm:text-base">
                  {t("_published_picture._total_result")}
                  <span className="py-1 rounded-full text-sm font-semibold">
                    {total || 0}
                  </span>
                </p>
              </div>

              <div className="flex justify-center sm:justify-start">
                <MemoizedPhotoSizeSelector
                  photoSize={photoSize}
                  onSizeChange={handlePhotoSizeChange}
                  loading={loading}
                  isDownloading={isDownloading}
                />
              </div>
            </div>
            <div
              id="picture-downloader"
              className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3"
            >
              {selectedIds.size > 0 && (
                <span className="text-sm sm:text-base order-first sm:order-none">
                  {selectedIds.size}件選択中
                </span>
              )}
              <div className="flex gap-2">
                {isSelecting ? (
                  <div data-testid="btn-cancel" aria-disabled={false}>
                    <button
                      onClick={() => {
                        setIsSelecting(false);
                        setSelectedIds(new Set());
                      }}
                      disabled={false}
                      className="px-3 py-2 text-sm sm:text-base border text-white bg-gray-500 rounded-md focus:outline-none focus:ring-2 disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors duration-200 ease-out hover:bg-gray-600 hover:border-gray-600"
                    >
                      {LABELS.CANCEL}
                    </button>
                  </div>
                ) : (
                  <div data-testid="btn-select" aria-disabled={false}>
                    <button
                      onClick={() => setIsSelecting(true)}
                      disabled={false}
                      className="px-3 py-2 text-sm sm:text-base border border-red-600 text-red-600 rounded-md hover:bg-red-50 hover:border-red-700 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 disabled:border-gray-400 disabled:text-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors duration-200 ease-out"
                    >
                      {LABELS.SELECT}
                    </button>
                  </div>
                )}
                <div
                  data-testid="btn-bulk-download"
                  aria-disabled={
                    !isSelecting || selectedIds.size === 0 || isDownloading
                  }
                >
                  <button
                    className={`px-3 py-2 text-sm sm:text-base rounded-md flex items-center justify-center gap-1 sm:gap-2 focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors duration-200 ease-out ${
                      !isSelecting || selectedIds.size === 0 || isDownloading
                        ? "bg-gray-500 text-white border border-gray-500 cursor-not-allowed"
                        : "bg-transparent border text-red-600 border-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-700"
                    }`}
                    disabled={
                      !isSelecting || selectedIds.size === 0 || isDownloading
                    }
                    onClick={async () => {
                      // Check if there are items to download in selection mode
                      if (isSelecting && selectedIds.size === 0) {
                        alert(LABELS.NO_PHOTOS_SELECTED);
                        return;
                      }

                      try {
                        setIsDownloading(true);
                        // Get download targets using our pure function
                        const downloadTargets = getDownloadTargets(
                          pictures,
                          selectedIds,
                          isSelecting,
                        );

                        // Prepare the zip file
                        const zipResult = await prepareZip(downloadTargets);

                        // Trigger the download
                        const today = new Date();
                        const yyyy = today.getFullYear();
                        const mm = String(today.getMonth() + 1).padStart(
                          2,
                          "0",
                        );
                        const dd = String(today.getDate()).padStart(2, "0");
                        const dateStr = `${yyyy}${mm}${dd}`;
                        downloadBlob(zipResult.blob, `${dateStr}.zip`);
                      } catch (error) {
                        alert(LABELS.DOWNLOAD_ERROR);
                      } finally {
                        setIsDownloading(false);
                      }
                    }}
                  >
                    <FaDownload
                      size={16}
                      className={
                        !isSelecting || selectedIds.size === 0 || isDownloading
                          ? "text-white"
                          : "text-red-600"
                      }
                    />
                    <span className="hidden sm:inline">
                      {LABELS.BULK_DOWNLOAD}
                    </span>
                    <span className="sm:hidden">DL</span>
                    {isDownloading && (
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1">
          <Container>
            <div
              className={`h-full overflow-auto relative pt-1 pl-1 ${
                isSelecting ? "thumbnail-area" : ""
              }`}
              style={{
                minHeight: "200px",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <div className="w-full h-full absolute">
                {loading && <LoadingDialog />}
                {pictures && (
                  <div className="flex flex-wrap gap-[1px]">
                    {pictures.length === 0 && (
                      <p className="text-sm sm:text-base">
                        {t("_generic._no_data")}
                      </p>
                    )}
                    {pictures.map((picture) => renderThumbnail(picture))}
                    {renderMoreContentIndicator()}
                  </div>
                )}
              </div>
            </div>
          </Container>
        </div>
        {/* Pagination */}
        {pictures && total > PICTURE_PAGE_SIZE && (
          <Pagination
            currentPage={page}
            pageSize={PICTURE_PAGE_SIZE}
            totalItems={total}
            onChangePage={(newPage: number) => {
              setPage(newPage);
              fetchPictures(newPage);
            }}
          />
        )}
      </div>
      {viewIndex >= 0 && pictures[viewIndex] && (
        <PictureDialog
          picture={pictures[viewIndex]}
          onPrevious={viewIndex > 0 ? () => viewPreviousPicture() : null}
          onNext={
            viewIndex < pictures.length - 1 ? () => viewNextPicture() : null
          }
          onClose={() => setViewIndex(-1)}
        />
      )}
    </div>
  );
};

export default Pictures;
